import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { db } from "astrid-firebase";
import organizationTypes from "astrid-firestore/src/api/organizations/constants/organizationTypes";
import useCollectionDataOnce from "astrid-firestore/src/hooks/useCollectionDataOnce";
import useDocumentDataOnce from "astrid-firestore/src/hooks/useDocumentDataOnce";
import useLoadingValues from "astrid-firestore/src/hooks/useLoadingValues";

import LoadingContext from "../../../ui/components/LoadingContext/LoadingContext";
import PageView from "../../../ui/views/PageView/PageView";

import { useProfile } from "../../../authentication/state/profile";
import featureFlags from "../../../authorization/constants/featureFlags";
import useFeatureFlag from "../../../authorization/hooks/useFeatureFlag";
import useOrganizationsQuery from "../../../organizations/hooks/useOrganizationsQuery";
import useSelectOrganization from "../../../organizations/hooks/useSelectOrganization";
import useUserOrganizations from "../../../organizations/hooks/useUserOrganizations";

import CreateProductionProducerForm from "../../components/CreateProductionForm/CreateProductionProducerForm";
import CreateProductionProducerFormNew from "../../components/CreateProductionForm/CreateProductionProducerFormNew";

export default function CreateProductionProducerView({ organization, articleId, workId }) {
	const { t } = useTranslation();

	const profile = useProfile();
	const selectOrganization = useSelectOrganization();

	const hasFlatteningFeature = useFeatureFlag(featureFlags.FLATTENING, organization);

	const [[work, article], loading, error] = useLoadingValues(
		useDocumentDataOnce(workId && db.collection("works").doc(workId)),
		useDocumentDataOnce(articleId && db.collection("articles").doc(articleId)),
	);

	// change global organization when producer is changed in form
	const setProducerId = useCallback(
		(id) => {
			if (id !== organization.id) {
				selectOrganization({ id, type: organizationTypes.PRODUCER });
			}
		},
		[organization.id, selectOrganization],
	);

	// get publishers for producer
	const [publishers] = useCollectionDataOnce(
		useOrganizationsQuery({
			producer: organization.id,
			type: organizationTypes.PUBLISHER,
		}),
	);

	const producerOptions = useUserOrganizations(profile)
		.filter(({ type }) => type === organizationTypes.PRODUCER)
		.map(({ id, name }) => ({ key: id, value: id, text: name }));

	const publisherOptions = publishers?.map(({ id, name }) => ({ key: id, value: id, text: name }));

	if (!publisherOptions) {
		return null;
	}

	return (
		<PageView maxWidth={800} header={t("createNewProd")}>
			{hasFlatteningFeature ? (
				<LoadingContext loading={loading} error={error}>
					<CreateProductionProducerFormNew publishers={publishers} work={work} article={article} />
				</LoadingContext>
			) : (
				<CreateProductionProducerForm
					profile={profile}
					setProducerId={setProducerId}
					producerOptions={producerOptions}
					publisherOptions={publisherOptions}
					defaultProducerId={organization.id}
					defaultPublisherId={publisherOptions?.length === 1 ? publisherOptions[0].value : null}
					enableProductionCategory={organization.enableProductionCategory}
				/>
			)}
		</PageView>
	);
}
